// src/components/ExtendedHoursToggle.jsx
//
// Toggle for trading during pre-market (4:00am-9:30am ET) and after-hours
// (4:00pm-8:00pm ET) sessions on Alpaca.
//
// Only renders for stock_equity bots. For crypto, returns null (crypto
// trades 24/7 — extended hours concept doesn't apply).
//
// Output value is a boolean which the parent should write to
// trade_info.alpaca.extended_hours.
//
// Note: Alpaca extended hours has constraints:
//   - LIMIT orders only (not MARKET)
//   - DAY time-in-force only
//   - Reduced liquidity, wider spreads
//
// We surface these warnings inline so users understand the tradeoff.

import React from 'react';
import {
  Paper, Box, Typography, Switch, FormControlLabel, Alert, Stack, Chip,
} from '@mui/material';
import { Schedule, WarningAmber } from '@mui/icons-material';


export function ExtendedHoursToggle({
  assetClass,
  value,
  onChange,
  orderExecutionMode,   // 'market' | 'limit' | 'aggressive_limit'
}) {
  // Crypto has no concept of market hours — don't render at all
  if (assetClass !== 'stock_equity') return null;

  const isMarketOrder = orderExecutionMode === 'market';
  const enabled = !!value && !isMarketOrder;
  const disabledReason = isMarketOrder
    ? 'Extended hours requires LIMIT orders. Switch order execution to "Limit" to enable.'
    : null;

  return (
    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#0f1218', borderColor: 'rgba(255,255,255,0.06)' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Schedule sx={{ fontSize: 20, color: enabled ? '#4d8fc7' : '#666', mt: 0.5 }} />
        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Extended Hours Trading
            </Typography>
            <Chip
              size="small"
              label="ALPACA ONLY"
              sx={{
                height: 18,
                fontSize: '0.6rem',
                fontWeight: 700,
                bgcolor: '#1e2a4a',
                color: '#9cc3e4',
              }}
            />
          </Box>
          <Typography variant="caption" sx={{ color: '#9aa6bd', display: 'block', mb: 1.5 }}>
            Allow this bot to trade during pre-market (4:00–9:30 ET) and after-hours (16:00–20:00 ET) sessions.
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(e) => onChange?.(e.target.checked)}
                disabled={isMarketOrder}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                {enabled ? 'Trading in pre/post-market enabled' : 'Regular hours only (9:30–16:00 ET)'}
              </Typography>
            }
            sx={{ ml: 0 }}
          />

          {disabledReason && (
            <Alert
              severity="info"
              icon={<WarningAmber sx={{ fontSize: 18 }} />}
              sx={{ mt: 1.5, fontSize: '0.75rem', py: 0.5 }}
            >
              {disabledReason}
            </Alert>
          )}

          {enabled && (
            <Stack spacing={0.5} sx={{ mt: 1.5 }}>
              <Typography variant="caption" sx={{ color: '#d9a441', fontSize: '0.7rem' }}>
                ⚠ Wider spreads and reduced liquidity outside regular hours
              </Typography>
              <Typography variant="caption" sx={{ color: '#d9a441', fontSize: '0.7rem' }}>
                ⚠ Some symbols (recent IPOs, low volume) may not be tradable
              </Typography>
              <Typography variant="caption" sx={{ color: '#d9a441', fontSize: '0.7rem' }}>
                ⚠ Time-in-force forced to DAY (orders not carried overnight)
              </Typography>
            </Stack>
          )}
        </Box>
      </Box>
    </Paper>
  );
}
